import React, { FC, Suspense, useMemo, useState } from 'react';
import {
  CatSkeleton,
  CatTableCell,
  CatTableRow
} from 'components/blocks-components';
import GetGroupPartType from '../../group-parts-type';
import { fetchPrice } from 'services/parts-diagram/actions';
import TableButtons from '../table-buttons/table-buttons';
import Price from 'components/price';
import { Image } from 'components/image';
import { ProductDealerPriceAvailability } from 'services/dealerPriceAvailability/types';
import { usePartDiagram } from 'hooks/use-part-diagram/parts-diagram-context';
import { LineItem, PartsTableProps } from 'types/parts-diagram';
import QuantityInput from 'components/quantity-Input';
import PartsAvailabilityWrapper from 'components/parts-availability-wrapper';

const PartsTableBody: FC<PartsTableProps> = ({
  lineItem,
  index,
  currency,
  handlePartView,
  handleOpen,
  setDrawerActive,
  shouldHideBtn
}) => {
  const { isCatCorp } = usePartDiagram();
  const {
    partNumber = '',
    name = '',
    quantity = '',
    ieSystemControlNumber = '',
    priceAndAvailabilityData = {} as ProductDealerPriceAvailability,
    disambiguation = false,
    imageIdentifiers
  } = lineItem || ({} as LineItem);
  const isArrangement = Object.keys(priceAndAvailabilityData).length === 0;
  const [modifiedPriceData, setModifiedPriceData] =
    useState<ProductDealerPriceAvailability>();
  const [disableRow, setDisableRow] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [modifiedQuantity, setModifiedQuantity] = useState<string>(
    quantity.trim().length === 0 ? '1' : quantity
  );

  const priceAvailability = useMemo(() => {
    return (
      <PartsAvailabilityWrapper
        availabilityData={
          partNumber === modifiedPriceData?.partNumber
            ? modifiedPriceData
            : priceAndAvailabilityData
        }
        shouldDisplayLabel={false}
        isLoading={isLoading}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modifiedPriceData, isLoading]);

  const priceCardSection = useMemo(() => {
    return (
      <Price
        currency={currency}
        price={
          partNumber === modifiedPriceData?.partNumber
            ? modifiedPriceData
            : priceAndAvailabilityData
        }
        showDiscountSection={false}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modifiedPriceData, isLoading]);

  return (
    <CatTableRow>
      <CatTableCell width="210px">
        <QuantityInput
          inputValue={modifiedQuantity}
          disabled={isArrangement || isCatCorp || disableRow}
          handleQuantityChange={async (value) => {
            setModifiedQuantity(value);
            setDisableRow(true);
            setIsLoading(true);
            const PriceData = await fetchPrice([
              { partNumber: partNumber, quantity: value }
            ]);
            setModifiedPriceData(PriceData?.response?.priceAndAvailability[0]);
            setDisableRow(false);
            setIsLoading(false);
          }}
        />
      </CatTableCell>
      <CatTableCell width="140px">
        {!disambiguation && ieSystemControlNumber === null && (
          <div>
            <Image
              alt={partNumber}
              src={imageIdentifiers}
              width={80}
              height={80}
            />
          </div>
        )}
      </CatTableCell>
      <CatTableCell>
        <GetGroupPartType
          partNumber={partNumber}
          name={name}
          disambiguation={disambiguation}
          ieSystemControlNumber={ieSystemControlNumber}
          price={modifiedPriceData ?? lineItem.priceAndAvailabilityData}
          handleOpen={handleOpen}
          lineItem={
            modifiedPriceData
              ? {
                  ...lineItem,
                  quantity: modifiedQuantity.toString(),
                  priceAndAvailabilityData: modifiedPriceData
                }
              : lineItem
          }
          setDrawerActive={setDrawerActive}
        />
      </CatTableCell>
      {!isCatCorp ? (
        <Suspense
          fallback={
            !disambiguation && ieSystemControlNumber === null ? (
              <CatSkeleton className="p-2" size="lg" />
            ) : (
              <CatTableCell></CatTableCell>
            )
          }
        >
          <CatTableCell>{priceAvailability}</CatTableCell>
        </Suspense>
      ) : (
        <CatTableCell></CatTableCell>
      )}
      {!isCatCorp ? (
        <Suspense
          fallback={
            !disambiguation && ieSystemControlNumber === null ? (
              <CatSkeleton className="p-2" size="lg" />
            ) : (
              <CatTableCell width="150px"></CatTableCell>
            )
          }
        >
          <CatTableCell align="right" width="150px">
            {priceCardSection}
          </CatTableCell>
        </Suspense>
      ) : (
        <CatTableCell width="150px"></CatTableCell>
      )}
      <CatTableCell width="150px">
        <TableButtons
          index={index}
          lineItem={lineItem}
          quantity={modifiedQuantity}
          handlePartView={handlePartView}
          disableRow={disableRow}
          shouldHideBtn={shouldHideBtn}
        />
      </CatTableCell>
    </CatTableRow>
  );
};

export default PartsTableBody;
